import { useCallback, useEffect, useMemo, useState } from "react"

import {
	type PersonalizedCourse,
	type CourseTag,
	type DetailedCoursePage,
	type UserCoursePageInput,
} from "@forento/shared/models/course"
import { toPriceString } from "@forento/shared/utilities/currency"

import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/PlatformContext"
import { useUser } from "~/contexts/UserContext"
import { useTranslation } from "~/translations"
import trpc, { query } from "~/utilities/trpc"

type CourseDetailButton =
	| { variant: "start" | "continue"; expireDate: Date | null }
	| { variant: "membership" | "paused" | "bokadero-membership" }
	| { variant: "purchase"; price: { amount: number; currency: string }; accessTimeMonths: number | null }
export type CourseDetail =
	| { status: "error" | "loading" | "not-found" }
	| {
			status: "success"
			title: string
			button: CourseDetailButton
			course: PersonalizedCourse
			inputs: UserCoursePageInput[]
	  }

type Current =
	| { status: "locked" }
	| { status: "not-dripped"; dripDate: Date; page: { id: number } }
	| { status: "unlocked"; page: DetailedCoursePage }

export type Course = {
	id: number
	title: string
	shortDescription: string
	thumbnailFilePath: string | null
	tagIds: number[]
	progress: number | undefined
	pagesLabel: string
	priceLabel: { color: "orange" | "blue" | "green"; value: string }
}

type UseCoursesResult = { status: "error" | "loading" } | { status: "success"; tags: CourseTag[]; data: Course[] }
export function useCourses(): UseCoursesResult {
	const t = useTranslation()

	const { data: courses, error: coursesError } = query.course.listPersonalized.useQuery()
	const { data: tags, error: tagsError } = query.course.listTags.useQuery()

	return useMemo(() => {
		if (coursesError || tagsError) return { status: "error" }
		if (courses === undefined || tags === undefined) return { status: "loading" }

		return {
			status: "success",
			tags,
			data: courses.map(course => ({
				id: course.id,
				title: course.title,
				shortDescription: course.shortDescription,
				thumbnailFilePath: course.thumbnailFilePath,
				tagIds: course.tags.map(tag => tag.id),
				progress: course.status === "unlocked" ? course.progress : undefined,
				pagesLabel:
					course.status === "unlocked" && course.pages.completed > 0
						? `${course.pages.completed}/${course.pages.total}`
						: `${t("course.numberOfPages", { count: course.pageCount })}`,
				priceLabel: (() => {
					if (course.status === "unlocked") {
						if (course.progress === 1) {
							return { color: "green" as const, value: t("course.completed") }
						}
						if (course.unlockedCause === "purchased" || course.unlockedCause === "membership") {
							return { color: "blue" as const, value: t("product.owned") }
						}
						if (course.unlockedCause === "tester") {
							return { color: "blue" as const, value: t("product.tester") }
						}
					}
					if (course.status === "bokadero-membership-required") {
						return { color: "orange" as const, value: "Gratis för medlemmar" }
					}
					if (course.price) {
						return { color: "orange" as const, value: toPriceString(course.price) }
					}
					if (course.membershipIds.length > 0) {
						return { color: "orange" as const, value: t("membership.title") }
					}
					return { color: "orange" as const, value: t("product.priceFree") }
				})(),
			})),
		}
	}, [courses, coursesError, t, tags, tagsError])
}

export function useCourseDetail(courseId: number): [CourseDetail, () => Promise<unknown>] {
	const platform = usePlatform()
	const user = useUser().user

	const {
		data: courseData,
		error: courseError,
		refetch: courseRefetch,
	} = query.course.getPersonalized.useQuery(courseId)
	const { data: inputsData, refetch: inputsRefetch } = query.course.listUserInputsByCourseId.useQuery(courseId, {
		enabled: user !== null,
	})

	return useMemo(() => {
		const refetch = () => Promise.all([courseRefetch(), inputsRefetch()])

		if (courseError) return [{ status: "error" }, refetch]
		if (courseData === undefined || (inputsData === undefined && user !== null))
			return [{ status: "loading" }, refetch]
		if (courseData === null) return [{ status: "not-found" }, refetch]

		const title = `${courseData.title} - ${platform.platform.title ?? platform.platform.name}`

		const button: CourseDetailButton = (() => {
			switch (courseData.status) {
				case "unlocked": {
					const expireDate = courseData.unlockedCause === "purchased" ? courseData.expireDate : null
					return courseData.progress > 0
						? { variant: "continue", expireDate }
						: { variant: "start", expireDate }
				}
				case "paywall":
					return {
						variant: "purchase",
						price: courseData.price,
						accessTimeMonths: courseData.accessTimeMonths,
					}
				case "membership-required":
					return { variant: "membership" }
				case "bokadero-membership-required":
					return { variant: "bokadero-membership" }
				case "paused":
					return { variant: "paused" }
			}
		})()

		return [{ status: "success", title, button, course: courseData, inputs: inputsData ?? [] }, refetch]
	}, [
		courseError,
		courseData,
		inputsData,
		user,
		platform.platform.title,
		platform.platform.name,
		courseRefetch,
		inputsRefetch,
	])
}

export function useCoursePlayer(courseId: number) {
	const user = useUser()
	const alert = useAlert()

	const [current, setCurrent] = useState<Current | null>()
	const [selection, setSelection] = useState<number | number[] | string>()

	const { data: course, error, refetch } = query.course.getPersonalized.useQuery(courseId)

	const isNextPageEnabled: boolean = (() => {
		if (current?.status !== "unlocked") return false
		if (current.page.type === "quiz") {
			if (current.page.quiz.type === "single-answer") return typeof selection === "number"
			else return Array.isArray(selection) && selection.length > 0
		}
		if (current.page.type === "input") {
			return typeof selection === "string" && selection.trim().length > 0
		}
		return true
	})()

	const loadPage = useCallback(
		async (page: Current | null) => {
			if (course == null || course.status !== "unlocked") return

			if (
				current?.status === "unlocked" &&
				page !== null &&
				course.firstUncompletedPage?.id === current.page.id
			) {
				await refetch()
			}

			setSelection(undefined)
			setCurrent(page)
		},
		[course, current, refetch],
	)

	const setPageById = useCallback(async (id: number) => {
		const page = await trpc.course.getPage.query(id)
		if (page === null) return

		setSelection(undefined)
		setCurrent(page)
	}, [])

	const previousPage = useCallback(async () => {
		if (course == null || current === undefined) return
		const previousPage = await (() => {
			if (current === null) return trpc.course.getFinalPageByCourseId.query(course.id)
			if (current.status === "locked") return trpc.course.getFirstUncompletedPageByCourseId.query(course.id)
			return trpc.course.getPreviousPageByPageId.query(current.page.id)
		})()
		if (previousPage === null) return

		setSelection(undefined)
		setCurrent(previousPage)
	}, [course, current])

	const nextPage = useCallback(async () => {
		if (!isNextPageEnabled || current?.status !== "unlocked") return

		const response = await trpc.course.completePage.mutate({
			id: current.page.id,
			selection: current.page.type === "quiz" || current.page.type === "input" ? selection : undefined,
		})
		if (response.status === "incorrect") {
			await alert.show("Incorrect answer!", "The answer you provided is incorrect.")
			return
		}
		loadPage(response.next)
	}, [isNextPageEnabled, current, selection, loadPage, alert])

	const skipChapter = useCallback(async () => {
		if (!isNextPageEnabled || current?.status !== "unlocked") return

		const result = await alert.confirm("Skip chapter", "Are you sure you want to skip this chapter?")
		if (!result.result) return

		await trpc.course.skipChapter.mutate(current.page.chapter.id)

		loadPage(await trpc.course.getNextChapterByPageId.query(current.page.id))
		result.close()
	}, [isNextPageEnabled, current, alert, loadPage])

	useEffect(() => {
		if (user.user && course && !current && course.status === "unlocked") {
			trpc.course.getFirstUncompletedPageByCourseId.query(course.id).then(loadPage)
		}
	}, [user.user, course, loadPage, current])

	if (error) return { status: "error" as const }
	if (course === undefined || current === undefined) return { status: "loading" as const }
	if (course === null) return { status: "not-found" as const }
	if (course.status !== "unlocked") return { status: "not-found" as const }

	return {
		status: "success" as const,
		course,
		reloadCourse: refetch,
		current,
		isNextPageEnabled,
		nextPage,
		previousPage,
		skipChapter,
		setPageById,
		setSelection,
	}
}
